/**
 * HR · Manpower cost.
 *
 * What each person costs the airport a month and an hour, under Saudi law:
 * gross pay, the employer's GOSI, the end-of-service accrual, medical cover
 * and, for non-Saudis, the levy and the fees; an outsourced head at its
 * contract charge. Then where that money sits — by department, trade and cost
 * centre — overtime, and the one comparison that tells whether the register
 * is complete: the register's year against the Staff line of the finance
 * figures (Financial System), which the Cash desk reads too.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { hasPermission, PERMISSION } from "../../api/dataSource";
import { useHrCost, useHrPeople, useHrSetOvertime } from "../../api/queries";
import { useSession } from "../../app/SessionContext";
import { useScreenContext } from "../../assistant/screen";
import { Button, Card, EmptyState, Field, Input, Segmented, Select, Skeleton, Table } from "../../design/components";
import { lastMonths, type HrCost, type HrGroup } from "../../domain/hr";
import { t } from "../../i18n";
import { Page } from "../../layout/AppShell";
import { useFinance } from "../executive/FinanceForm";
import { useHrAccess } from "./access";
import { HourlySwitch, PersonLink, PreviewBar, useShowHourly } from "./kit";
import {
  ColumnChart,
  Coverage,
  HrError,
  money,
  rate,
  RulesBasis,
  s,
  SampleBanner,
  sar,
  SUITE,
  Tiles,
  tradeLabel,
} from "./shared";

type Tab = "components" | "departments" | "trades" | "centres" | "people" | "overtime";
const TABS: { value: Tab; label: string }[] = [
  { value: "components", label: "What it is made of" },
  { value: "departments", label: "By department" },
  { value: "trades", label: "By trade" },
  { value: "centres", label: "By cost center" },
  { value: "people", label: "Each person" },
  { value: "overtime", label: "Overtime" },
];

export function HrCostPage() {
  const session = useSession();
  const q = useHrCost();
  const [tab, setTab] = useState<Tab>("components");
  const access = useHrAccess();
  const showHourly = useShowHourly();
  const c = q.data;
  const costedRates = (c?.lines ?? []).filter((l) => l.hourlyRate != null).map((l) => l.hourlyRate as number);
  const meanRate = costedRates.length ? costedRates.reduce((a, b) => a + b, 0) / costedRates.length : null;

  useScreenContext(
    c
      ? {
          route: "/hr-cost",
          title: "Manpower cost",
          summary: [
            t("The workforce costs {m} a month, {y} a year, for {n} people.", {
              m: sar(c.totals.monthly),
              y: money(c.totals.annual),
              n: c.totals.heads,
            }),
            t(
              "Of that, GOSI is {g} a month and the end-of-service accrual {e}; the end-of-service owed today is {l}.",
              {
                g: sar(c.totals.gosiEmployer),
                e: sar(c.totals.eosbAccrual),
                l: money(c.totals.eosbLiability),
              },
            ),
            t("Overtime over the last twelve months: {h} hours, {c}.", {
              h: c.totals.overtimeHours12m.toLocaleString("en"),
              c: sar(c.totals.overtime12m),
            }),
          ],
          offers: [
            { label: t("Which department costs most?"), ask: "which department has the highest manpower cost?" },
            { label: t("What does an hour of each trade cost?"), ask: "what is the hourly labour rate by trade?" },
          ],
          worksInFull: true,
        }
      : null,
  );

  return (
    <Page
      eyebrow={t("{site} · {suite}", { site: session.site.code, suite: t(SUITE) })}
      title="Manpower cost"
      actions={<HourlySwitch />}
    >
      <PreviewBar />
      <SampleBanner />
      {!access.can.pay && <HrError error={Object.assign(new Error("Not permitted"), { status: 403 })} />}
      {q.isLoading && <Skeleton rows={8} />}
      {q.error && <HrError error={q.error} retry={() => void q.refetch()} />}
      {c && access.can.pay && (
        <>
          <Tiles
            items={[
              {
                key: "month",
                label: t("A month"),
                value: sar(c.totals.monthly),
                sub: t("{n} people costed", { n: c.coverage.costed }),
              },
              { key: "year", label: t("A year"), value: money(c.totals.annual), sub: t("before overtime") },
              {
                key: "head",
                label: t("A person, on average, a month"),
                value: sar(c.coverage.costed ? c.totals.monthly / c.coverage.costed : null),
                sub: t("salary and everything the airport pays on top"),
              },
              ...(showHourly
                ? [
                    {
                      key: "hour",
                      label: t("An hour, on average"),
                      value: rate(meanRate),
                      sub: t("the month spread over the hours actually worked"),
                    },
                  ]
                : []),
              {
                key: "eosb",
                label: t("End of service owed today"),
                value: money(c.totals.eosbLiability),
                sub: t("if every direct employee left now (art. 84)"),
              },
              {
                key: "ot",
                label: t("Overtime, 12 months"),
                value: sar(c.totals.overtime12m),
                sub: t("{h} hours", { h: c.totals.overtimeHours12m.toLocaleString("en") }),
                tone: c.totals.overtime12m > c.totals.monthly * 0.5 ? "warn" : undefined,
              },
            ]}
          />
          {c.coverage.people > 0 && (
            <Coverage
              lines={[
                { label: t("have pay or a contract charge"), of: c.coverage.people, n: c.coverage.costed },
                { label: t("are bound to a platform user"), of: c.coverage.people, n: c.coverage.boundToUser },
              ]}
            />
          )}
          <AgainstFinance c={c} />
          {c.coverage.people === 0 ? (
            <EmptyState>
              {t("No one is on the register yet. Start on the Workforce screen.")}
            </EmptyState>
          ) : (
            <>
              <Segmented value={tab} options={TABS} onChange={setTab} ariaLabel="Cost views" />
              {tab === "components" && <Components c={c} />}
              {tab === "departments" && <Groups title="By department" rows={c.byDepartment} hourly={showHourly} />}
              {tab === "trades" && <Groups title="By trade" rows={c.byTrade} trade hourly={showHourly} />}
              {tab === "centres" && <Groups title="By cost center" rows={c.byCostCentre} hourly={showHourly} />}
              {tab === "people" && <People c={c} hourly={showHourly} />}
              {tab === "overtime" && <Overtime c={c} />}
            </>
          )}
          <p className={s.note}>
            {t("How it is worked out:")} {t(c.method)}
          </p>
          <RulesBasis basis={c.rulesBasis} />
        </>
      )}
    </Page>
  );
}

/** The register's year against the Staff line of the finance figures. */
function AgainstFinance({ c }: { c: HrCost }) {
  const finance = useFinance().value;
  const staff = finance.opexByArea?.find((a) => a.key === "staff");
  if (!staff) return null;
  const annualised = finance.daysElapsed > 0 ? (staff.actual * 365) / finance.daysElapsed : staff.actual;
  const share = annualised > 0 ? Math.round((c.totals.annual / annualised) * 1000) / 10 : null;
  return (
    <Card
      title={t("Against the finance figures")}
      sub={
        finance.basis === "demo"
          ? t("Demo Staff figures until finance data is connected or entered.")
          : t("The Staff line of the finance figures, {period}.", { period: finance.period })
      }
      testId="hr-vs-finance"
    >
      <dl className={s.kv}>
        <dt>{t("Staff cost in the finance figures, a year")}</dt>
        <dd>
          {money(annualised)}{" "}
          <span className={s.dim}>({t("year to date {v}, annualised", { v: money(staff.actual) })})</span>
        </dd>
        <dt>{t("The register, a year")}</dt>
        <dd>{money(c.totals.annual + c.totals.overtime12m)}</dd>
        <dt>{t("The register covers")}</dt>
        <dd>
          <b>{share == null ? "—" : `${share}%`}</b>{" "}
          <span className={s.dim}>
            {share != null && share < 90
              ? t("— the rest isn't on the register yet (other departments, bonuses, training).")
              : share != null && share > 110
                ? t("— more than the ledger. Check for leavers still marked current.")
                : ""}
          </span>
        </dd>
      </dl>
      <p className={s.note}>
        <Link to="/cash">{t("Cash & Bottom Line")}</Link> · <Link to="/finance-system">{t("Finance system")}</Link>
      </p>
    </Card>
  );
}

function Components({ c }: { c: HrCost }) {
  const parts = [
    { key: "gross", label: t("Gross pay"), value: c.totals.gross },
    { key: "gosi", label: t("GOSI (employer)"), value: c.totals.gosiEmployer },
    { key: "eosb", label: t("End of service"), value: c.totals.eosbAccrual },
    { key: "medical", label: t("Medical"), value: c.totals.medical },
    { key: "expat", label: t("Levy and fees"), value: c.totals.expatFees },
    { key: "contract", label: t("Outsourced"), value: c.totals.contract },
  ];
  return (
    <Card
      title={t("A month's cost, by what it is made of")}
      sub={t("Monthly staff cost, before overtime.")}
    >
      <ColumnChart
        label={t("A month's cost, by what it is made of")}
        format={(v) => money(v).replace("SAR ", "")}
        data={parts}
      />
      <Table>
        <thead>
          <tr>
            <th>{t("Part")}</th>
            <th className={s.num}>{t("A month")}</th>
            <th className={s.num}>{t("Share")}</th>
          </tr>
        </thead>
        <tbody>
          {parts.map((p) => (
            <tr key={p.key}>
              <td>{p.label}</td>
              <td className={s.num}>{sar(p.value)}</td>
              <td className={s.num}>
                {c.totals.monthly ? `${Math.round((p.value / c.totals.monthly) * 1000) / 10}%` : "—"}
              </td>
            </tr>
          ))}
          <tr>
            <td>
              <b>{t("Total")}</b>
            </td>
            <td className={s.num}>
              <b>{sar(c.totals.monthly)}</b>
            </td>
            <td />
          </tr>
        </tbody>
      </Table>
    </Card>
  );
}

function Groups({ title, rows, trade, hourly }: { title: string; rows: HrGroup[]; trade?: boolean; hourly: boolean }) {
  return (
    <Card
      title={t(title)}
      sub={
        trade && hourly
          ? t("Rate used for labor cost when the worker isn't known.")
          : undefined
      }
    >
      <Table>
        <thead>
          <tr>
            <th>{trade ? t("Trade") : t("Name")}</th>
            <th className={s.num}>{t("People")}</th>
            <th className={s.num}>{t("A month")}</th>
            <th className={s.num}>{t("A year")}</th>
            {hourly && <th className={s.num}>{t("An hour, on average")}</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((g) => (
            <tr key={g.key}>
              <td>{trade ? tradeLabel(g.key) : g.key}</td>
              <td className={s.num}>{g.heads}</td>
              <td className={s.num}>{sar(g.monthly)}</td>
              <td className={s.num}>{money(g.annual)}</td>
              {hourly && <td className={s.num}>{rate(g.hourlyRate)}</td>}
            </tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
}

function People({ c, hourly }: { c: HrCost; hourly: boolean }) {
  return (
    <Card title={t("Each person")} sub={t("Monthly cost by part, and the hourly rate.")}>
      <Table>
        <thead>
          <tr>
            <th>{t("Name")}</th>
            <th>{t("Department")}</th>
            <th className={s.num}>{t("Gross")}</th>
            <th className={s.num}>{t("GOSI")}</th>
            <th className={s.num}>{t("End of service")}</th>
            <th className={s.num}>{t("Medical")}</th>
            <th className={s.num}>{t("Levy / contract")}</th>
            <th className={s.num}>{t("A month")}</th>
            {hourly && <th className={s.num}>{t("An hour")}</th>}
            <th className={s.num}>{t("Overtime hour")}</th>
          </tr>
        </thead>
        <tbody>
          {c.lines.map((l) => (
            <tr key={l.employeeId}>
              <td>
                <PersonLink id={l.employeeId} name={l.name} />
                {!l.costed && <span className={s.tag}>{t("not costed")}</span>}
              </td>
              <td>{l.department ?? "—"}</td>
              <td className={s.num}>{l.employment === "outsourced" ? "—" : sar(l.gross)}</td>
              <td className={s.num} title={l.gosiEmployerPct != null ? `${l.gosiEmployerPct}%` : undefined}>
                {l.employment === "outsourced" ? "—" : sar(l.gosiEmployer)}
              </td>
              <td className={s.num}>{l.employment === "outsourced" ? "—" : sar(l.eosbAccrual)}</td>
              <td className={s.num}>{l.employment === "outsourced" ? "—" : sar(l.medical)}</td>
              <td className={s.num}>
                {l.employment === "outsourced" ? sar(l.contract) : l.expatFees ? sar(l.expatFees) : "—"}
              </td>
              <td className={s.num}>
                <b>{sar(l.monthlyCost)}</b>
              </td>
              {hourly && <td className={s.num}>{rate(l.hourlyRate)}</td>}
              <td className={s.num}>{rate(l.overtimeRate)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
}

function Overtime({ c }: { c: HrCost }) {
  const session = useSession();
  const canEdit = hasPermission(session.principal, PERMISSION.hrWrite);
  return (
    <>
      <Card
        title={t("Overtime by month")}
        sub={t("Art. 107 rate: hourly wage plus half the hourly basic.")}
      >
        <ColumnChart
          label={t("Overtime by month")}
          format={(v) => money(v).replace("SAR ", "")}
          data={c.overtime.months.map((m) => ({ key: m.month, label: m.month.slice(2), value: m.cost }))}
        />
      </Card>
      <div className={s.split}>
        <Card title={t("Who works the most overtime")}>
          {c.overtime.people.length === 0 ? (
            <EmptyState>{t("No overtime recorded in the last twelve months.")}</EmptyState>
          ) : (
            <Table>
              <thead>
                <tr>
                  <th>{t("Name")}</th>
                  <th className={s.num}>{t("Hours")}</th>
                  <th className={s.num}>{t("Cost")}</th>
                </tr>
              </thead>
              <tbody>
                {c.overtime.people.map((p) => (
                  <tr key={p.employeeId}>
                    <td>
                      <PersonLink id={p.employeeId} name={p.name} />
                    </td>
                    <td className={s.num}>{p.hours}</td>
                    <td className={s.num}>{sar(p.cost)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
        {canEdit && <RecordOvertime />}
      </div>
    </>
  );
}

function RecordOvertime() {
  const people = useHrPeople();
  const save = useHrSetOvertime();
  const months = lastMonths(new Date(), 12).reverse();
  const [emp, setEmp] = useState("");
  const [month, setMonth] = useState(months[0]);
  const [hours, setHours] = useState("");
  const [done, setDone] = useState<string | null>(null);
  const h = Number(hours);
  const ok = emp && hours.trim() !== "" && h >= 0 && h <= 400;
  return (
    <Card title={t("Record overtime")} sub={t("One person, one month; entering 0 removes it.")}>
      <form
        className={s.form}
        onSubmit={(e) => {
          e.preventDefault();
          if (!ok) return;
          save.mutate(
            { employeeId: emp, month, hours: h },
            { onSuccess: () => setDone(t("Recorded: {h} hours in {m}.", { h, m: month })) },
          );
        }}
      >
        <Field label="Person">
          <Select value={emp} onChange={(e) => setEmp(e.target.value)} data-testid="hr-ot-person">
            <option value="">{t("Choose a person")}</option>
            {(people.data?.employees ?? [])
              .filter((e) => e.status !== "left" && e.employment === "direct")
              .map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name} · {e.employeeNo}
                </option>
              ))}
          </Select>
        </Field>
        <Field label="Month">
          <Select value={month} onChange={(e) => setMonth(e.target.value)}>
            {months.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Hours">
          <Input
            value={hours}
            onChange={(e) => setHours(e.target.value)}
            inputMode="decimal"
            data-testid="hr-ot-hours"
          />
        </Field>
        <Button type="submit" variant="primary" disabled={!ok || save.isPending}>
          {t("Record")}
        </Button>
      </form>
      {done && <p className={s.ok}>{done}</p>}
      {save.error && <p className={s.err}>{save.error instanceof Error ? save.error.message : String(save.error)}</p>}
    </Card>
  );
}
